import {DetailsField, DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {Trans, useTranslation} from '@/translations/i18n'
import {useOutletContext} from 'react-router-dom'
import {ProjectExtended} from '@/features/projects/types'
import {Text} from '@/components/ui/text/Text'
import {Grid} from '@/components/ui/grid-atoms/GridAtoms'
import {useGetPlannedAccessesDetails} from '@/features/planned-accesses/queries/useGetPlannedAccessesDetails'
import {PlannedActionSectionEmptyState} from './PlannedActionSectionEmptyState'
import {ErrorBox} from '@/components/commons/error-box/ErrorBox'
import {AlertCircleIcon} from '@/components/ui/icon/Icon'
import {usePlannedAccessesCalculations} from '../../hooks/usePlannedAccessesCalculations'

export const PlannedAccessesSection = () => {
    const {t} = useTranslation()
    const {project} = useOutletContext<{project: ProjectExtended}>()

    const {
        data: plannedAccesses,
        isLoading: isLoadingPlannedAccesses,
        isError: isErrorPlannedAccesses,
        error: errorPlannedAccesses
    } = useGetPlannedAccessesDetails({
        urlParams: {
            projectId: project.id
        }
    })

    // Use the custom hook for all planned access calculations
    const {
        totalPlannedAccesses,
        totalPlannedAccessesInAbsence,
        totalPlannedAccessesInPresence,
        totalPlannedTreatmentsForWeek,
        totalPlannedTreatmentsForMonth
    } = usePlannedAccessesCalculations(project.durationDays ?? 0, plannedAccesses)

    if (isErrorPlannedAccesses && errorPlannedAccesses?.response?.data.code !== 'project-planned-access-not-found') {
        return <ErrorBox icon={<AlertCircleIcon />} title={t('errors:default')} />
    }

    if (isLoadingPlannedAccesses || !plannedAccesses) {
        return <PlannedActionSectionEmptyState isLoading={isLoadingPlannedAccesses} />
    }
    return (
        <DetailsFieldsSection
            variant="neutral"
            title={t('projects:planned_accesses')}
            headingEndSlot={
                <Text palette="neutral" shade="500" size="textSm">
                    <Trans
                        i18nKey="commons:total_score_x_bolded"
                        components={{
                            b: <Text render="span" palette="neutral" shade="500" size="textSm" weight="500" />
                        }}
                        values={{count: totalPlannedAccesses}}
                    />
                </Text>
            }
        >
            <Grid columns={2} gap={3} fullWidth>
                <DetailsField fullWidth>
                    <Text palette="neutral" shade="500" size="textSm">
                        {t('projects:in_presence')}
                    </Text>
                    <Text palette="neutral" shade="950" size="textSm" weight="500">
                        {totalPlannedAccessesInPresence}
                    </Text>
                </DetailsField>
                <DetailsField fullWidth>
                    <Text palette="neutral" shade="500" size="textSm">
                        {t('projects:in_absence')}
                    </Text>
                    <Text palette="neutral" shade="950" size="textSm" weight="500">
                        {totalPlannedAccessesInAbsence}
                    </Text>
                </DetailsField>
                <DetailsField fullWidth>
                    <Text palette="neutral" shade="500" size="textSm">
                        {t('projects:therapies_per_week')}
                    </Text>
                    <Text palette="neutral" shade="950" size="textSm" weight="500">
                        {totalPlannedTreatmentsForWeek}
                    </Text>
                </DetailsField>
                <DetailsField fullWidth>
                    <Text palette="neutral" shade="500" size="textSm">
                        {t('projects:therapies_per_month')}
                    </Text>
                    <Text palette="neutral" shade="950" size="textSm" weight="500">
                        {totalPlannedTreatmentsForMonth}
                    </Text>
                </DetailsField>
            </Grid>
        </DetailsFieldsSection>
    )
}
